/**
 * useFeaturedHosts – fetch published producers for the landing host carousel.
 * Returns up to `limit` published producers with cover images resolved via media_assets SoT.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';

export interface FeaturedHost {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  city: string | null;
  region: string | null;
  coverUrl: string | null;
  href: string;
}

export function useFeaturedHosts(limit = 8) {
  return useQuery<FeaturedHost[]>({
    queryKey: ['featured-hosts', limit],
    queryFn: async () => {
      const { data: producers, error } = await supabase
        .from('producers')
        .select('id, name, slug, short_description, city, region')
        .in('status', ['published', 'public'])
        .order('published_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      if (!producers || producers.length === 0) return [];

      // Batch-fetch cover images for all producers
      const ids = producers.map((p) => p.id);
      const { data: assets } = await supabase
        .from('media_assets')
        .select('*')
        .eq('owner_type', 'producer')
        .in('owner_id', ids)
        .eq('kind', 'cover')
        .order('sort_order', { ascending: true });

      const coverMap = new Map<string, string>();
      for (const a of (assets ?? []) as unknown as MediaAsset[]) {
        if (a.owner_id && !coverMap.has(a.owner_id)) {
          coverMap.set(a.owner_id, resolveAssetUrl(a, 640));
        }
      }

      return producers.map((p) => ({
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.short_description,
        city: p.city,
        region: p.region,
        coverUrl: coverMap.get(p.id) ?? null,
        href: `/producer/${p.slug || p.id}`,
      }));
    },
    staleTime: 10 * 60 * 1000,
  });
}
